import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2 } from 'lucide-react';
import type { IPhotostudioElement } from '@/@types/photostudio';
import type { Id } from '../../../convex/_generated/dataModel';

interface IElementDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  element: IPhotostudioElement | null;
  onConfirm: (elementId: Id<'photostudioElements'>) => Promise<{ success: boolean }>;
}

export function ElementDeleteDialog({
  open,
  onOpenChange,
  element,
  onConfirm,
}: IElementDeleteDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDelete = async () => {
    if (!element) return;
    setIsSubmitting(true);
    const result = await onConfirm(element._id);
    setIsSubmitting(false);
    if (result.success) {
      onOpenChange(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Element</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete &quot;{element?.name}&quot;? This action cannot be
            undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isSubmitting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" />}
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
